// Tiny in-memory + sessionStorage cache for public JD + form responses.
// Landing prefetches everything on mount so subsequent JD/Apply page
// navigations hydrate instantly; the pages still hit the API in the
// background to stay fresh.

import { api } from "./api";

const mem = { jd: new Map(), form: new Map(), role: new Map() };

const readStore = (key) => {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};
const writeStore = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
        // sessionStorage quota / disabled — cache stays in-memory only.
    }
};

export const getCachedJd = (id) => {
    if (mem.jd.has(id)) return mem.jd.get(id);
    const s = readStore(`jd:${id}`);
    if (s) mem.jd.set(id, s);
    return s;
};

export const getCachedRoleForm = (roleId) => {
    if (mem.form.has(roleId)) return mem.form.get(roleId);
    const s = readStore(`role_form:${roleId}`);
    if (s) mem.form.set(roleId, s);
    return s;
};

export const primeJdCache = (jd) => {
    if (!jd || !jd.id) return;
    mem.jd.set(jd.id, jd);
    writeStore(`jd:${jd.id}`, jd);
};

export const primeFormCache = (roleId, payload) => {
    if (!roleId || !payload) return;
    mem.form.set(roleId, payload);
    writeStore(`role_form:${roleId}`, payload);
};

// Called by Landing after `/public/openings` returns. For each role we
// fetch its JD + latest form in parallel and prime both caches so the
// next click loads instantly.
export const prefetchAllPublic = async (openings) => {
    const tasks = [];
    for (const role of openings || []) {
        for (const jd of role.descriptions || []) {
            tasks.push(
                api
                    .get(`/public/job-descriptions/${jd.id}`)
                    .then((r) => primeJdCache(r.data))
                    .catch(() => {}),
            );
        }
        tasks.push(
            api
                .get(`/public/openings/${role.id}`)
                .then((r) => primeFormCache(role.id, r.data))
                .catch(() => {}),
        );
    }
    await Promise.all(tasks);
};
